/* eslint-disable react/destructuring-assignment */
import React from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

import { useUser } from 'actions/useUser';
import { Role } from 'types/schema.type';

import { LoadingSpinner } from '../LoadingSpinner';
import { DOWNLOAD_SEQUENCES_BATCH } from '../../mutations';
import { GridCellExpand } from '../datagrid/GridCellExpand';
import { GridCustomToolbar } from '../datagrid/GridCustomToolbar';
import { CustomFilterPanel, FilterRow } from '../datagrid/CustomFilterPanel';
import { buildSequencesFilterInput } from './adminFilterInput';
import { ReanalyzeSelectedButton } from './reanalysis/ReanalyzeSelectedButton';

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  container: {
    width: 1400,
    marginTop: theme.spacing(4),
  },
  downloadButton: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

const getIsoTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);

  return date.toISOString();
};

const makeRows = (items) =>
  items.map((item) => ({
    id: item.id,
    userEmail: item.user?.email || '',
    userId: item.userId,
    status: item.status,
    recordingCount: item.recordingIds?.length || 0,
    startedAt: getIsoTime(item.startedAt),
    endedAt: getIsoTime(item.endedAt),
    // project_management#514: WHEN the stored analysis was produced. Without
    // this, a reanalysis is invisible — analysis output is deterministic, so a
    // successful re-run changes nothing else the operator can see, and the
    // table looks identical to before.
    analysedAt: getIsoTime(item.analysis?.analysedAt),
  }));

function renderCellExpand(params) {
  return (
    <GridCellExpand
      value={params.value ? params.value.toString() : ''}
      width={params.colDef.computedWidth}
    />
  );
}

const columns = [
  { field: 'id', headerName: 'Sequence ID', width: 220 },
  {
    field: 'userEmail',
    headerName: 'Email',
    width: 200,
    renderCell: renderCellExpand,
  },
  {
    field: 'userId',
    headerName: 'User ID',
    width: 220,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
  },
  {
    field: 'recordingCount',
    headerName: 'Recordings',
    width: 120,
  },
  {
    field: 'startedAt',
    headerName: 'Started At',
    width: 200,
  },
  {
    field: 'endedAt',
    headerName: 'Ended At',
    width: 200,
  },
  {
    // The only visible proof that a reanalysis actually ran (#514).
    field: 'analysedAt',
    headerName: 'Analysed At',
    width: 200,
  },
];

const SEQUENCE_FILTER_COLUMNS = [
  { field: 'id', headerName: 'Sequence ID' },
  { field: 'userEmail', headerName: 'Email' },
  { field: 'userId', headerName: 'User ID' },
  { field: 'status', headerName: 'Status' },
];

export const GET_ALL_SEQUENCES_PAGINATED = gql`
  query GetAllSequencesPaginated($offset: Int!, $limit: Int!, $filter: SequencesFilterInput) {
    getAllSequencesPaginated(offset: $offset, limit: $limit, filter: $filter) {
      success
      error
      totalCount
      sequences {
        id
        userId
        status
        startedAt
        endedAt
        recordingIds
        analysis {
          id
          analysedAt
        }
        user {
          id
          email
        }
      }
    }
  }
`;

export function SequenceManagement() {
  const classes = useStyles();
  const [selectedSequenceIds, setSelectedSequenceIds] = React.useState<string[]>([]);
  const [showDownloadButton, setShowDownloadButton] = React.useState(true);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);

  // Custom filter state
  const [customFilters, setCustomFilters] = React.useState<FilterRow[]>([]);
  const [debouncedFilters, setDebouncedFilters] = React.useState<FilterRow[]>(customFilters);

  // Debounce filter updates
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(customFilters);
    }, 500);

    return () => clearTimeout(handler);
  }, [customFilters]);

  // Pagination state
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [rowCountState, setRowCountState] = React.useState(0);

  const { user, loading: authLoading } = useUser();

  // Convert custom filters to backend filter input. Rows on the same column
  // are combined; list columns split on commas, spaces and new lines (#575).
  const filterInput = buildSequencesFilterInput(debouncedFilters);

  const { loading: dataLoading, error, data } = useQuery(GET_ALL_SEQUENCES_PAGINATED, {
    variables: {
      offset: page * pageSize,
      limit: pageSize,
      filter: Object.keys(filterInput).length ? filterInput : undefined,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [
    downloadSequencesBatch,
    { data: downloadData, loading: downloadLoading, error: downloadError },
  ] = useMutation(DOWNLOAD_SEQUENCES_BATCH);

  const downloadSuccess = downloadData?.downloadSequencesBatch?.success;

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      data?.getAllSequencesPaginated?.totalCount !== undefined
        ? data.getAllSequencesPaginated.totalCount
        : prevRowCountState,
    );
  }, [data?.getAllSequencesPaginated?.totalCount, setRowCountState]);

  React.useEffect(() => {
    if (downloadLoading) {
      setShowDownloadButton(false);
    }

    if (!downloadLoading && (downloadError || downloadSuccess === false)) {
      setShowError(true);
    }

    if (downloadSuccess) {
      setShowSuccess(true);
    }
  }, [downloadLoading, downloadError, downloadSuccess]);

  // Reset page to 0 when filters change
  React.useEffect(() => {
    setPage(0);
  }, [debouncedFilters]);

  if (authLoading || !user) return <div>Loading auth...</div>;

  if (user.role !== Role.Admin) return null;

  if (error && !data?.getAllSequencesPaginated?.sequences) {
    return <div>Error loading sequences: {error.message}</div>;
  }

  const response = data?.getAllSequencesPaginated;
  const sequences = response?.sequences ?? [];
  const rows = makeRows(sequences);

  const handleSelectChange = (ids: string[]) => setSelectedSequenceIds(ids);

  const handleDownload = () => {
    downloadSequencesBatch({
      variables: { sequenceIds: selectedSequenceIds },
    });
  };

  const handleAlertClose = () => {
    setShowDownloadButton(true);
    setShowError(false);
    setShowSuccess(false);
  };

  return (
    <>
      <Container className={classes.container} maxWidth={false}>
        {showSuccess && (
          <Alert
            severity="success"
            action={
              <Button color="inherit" size="small" onClick={handleAlertClose}>
                CLOSE
              </Button>
            }
          >
            <AlertTitle>Success</AlertTitle>
            Download link will be sent to {user.email}
          </Alert>
        )}
        {showError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleAlertClose}>
                CLOSE
              </Button>
            }
          >
            <AlertTitle>Error</AlertTitle>
            {downloadData?.downloadSequencesBatch?.error}
          </Alert>
        )}
        {downloadLoading && <LoadingSpinner />}
        {showDownloadButton && (
          <Button
            className={classes.downloadButton}
            variant="contained"
            color="primary"
            disabled={!selectedSequenceIds.length}
            onClick={handleDownload}
          >
            Download selected
          </Button>
        )}
        {/* project_management#514 — same selection, paced bulk reanalysis. */}
        <ReanalyzeSelectedButton
          className={classes.downloadButton}
          scope="sequence"
          selected={rows
            .filter((row) => selectedSequenceIds.includes(row.id))
            .map((row) => ({ id: row.id }))}
        />
        <CustomFilterPanel
          filters={customFilters}
          columns={SEQUENCE_FILTER_COLUMNS}
          onFiltersChange={setCustomFilters}
        />
        <div style={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            components={{
              Toolbar: GridCustomToolbar,
            }}
            rowCount={rowCountState}
            loading={dataLoading}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            pagination
            autoHeight
            checkboxSelection
            disableSelectionOnClick
            disableColumnFilter
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSelectionModelChange={(newSelection) => handleSelectChange(newSelection as string[])}
          />
        </div>
      </Container>
    </>
  );
}
